import React, { useRef, useEffect, useState } from "react";
import mapboxgl, { Expression, Map as MapboxMap } from "mapbox-gl";
import MapboxGLWorker from 'mapbox-gl/dist/mapbox-gl-csp-worker?worker';
import "mapbox-gl/dist/mapbox-gl.css";
import * as d3 from "d3";
import { lightModeNamedColors } from "theme/infegyColor";
import { QueryInfo } from "system/query/QueryInfo";
import { AutoWidgetQueryBehavior, getSingleWidgetResponseByName, WidgetRequest } from "system/widget/Widget";
import withQueryWidget, { WithQueryWidgetProps } from "components/widgetParts/queryWidgetComposition/withQueryWidget";

const defaultWidgetRequest = {
  name: 'choropleth',
  endpoint: 'query/agg',
  queryParams: { },
  autoWidgetQueryBehavior: AutoWidgetQueryBehavior.AUTO_SINGLE
} as WidgetRequest;

export function BuildDefaultChoroplethRequests(queryInfo: QueryInfo): any {
  const widgetRequest : WidgetRequest = { ...defaultWidgetRequest };
  const requestInitBody = { aggs: {} };

  const foundCountryField = queryInfo.fields?.find((field) => field.id === "country") || false;
  if (foundCountryField) {
    requestInitBody.aggs = {
      "Countries": {
        "op": "keyword",
        "field": "country",
        "bucket_count": 195,
        "aggs": {
          "Sentiment": {
            "op": "keyword",
            "field": "sentiment",
            "filter": ["p", "n"],
            "aggs": {
              "percent": {
                "op": "percent",
                "of_total": true
              }
            }
          }
        }
      }
    };
  }
  widgetRequest.requestInit = { body: requestInitBody };
  return [widgetRequest];
}


//   const foundCountryField = queryInfo.fields?.find((field) => field.id === "country") || false;
//   if (foundCountryField) {
//     let aggs = {
//       "aggs": {
//         "Countries": {
//           "op": "keyword",
//           "field": "country",
//           "bucket_count": 195,
//           "aggs": {
//             "Sentiment": {
//               "op": "keyword",
//               "field": "sentiment",
//               "filter": ["p", "n"],
//               "aggs": {
//                 "percent": {
//                   "op": "percent",
//                   "of_total": true
//                 }
//               }
//             }
//           }
//         }
//       }
//     }
//     return aggs;
//   }
//   return;
// }

// Assign worker class for Mapbox GL
(mapboxgl as any).workerClass = MapboxGLWorker;

enum MapboxStyle {
    Streets = "mapbox://styles/mapbox/streets-v11",
    Outdoors = "mapbox://styles/mapbox/outdoors-v11",
    Light = "mapbox://styles/mapbox/light-v10",
    Dark = "mapbox://styles/mapbox/dark-v10",
    Satellite = "mapbox://styles/mapbox/satellite-v9",
    SatelliteStreets = "mapbox://styles/mapbox/satellite-streets-v11",
    NavigationDay = "mapbox://styles/mapbox/navigation-day-v1",
    NavigationNight = "mapbox://styles/mapbox/navigation-night-v1"
}

// type CountryAggKey = "country";
// type ChoroplethAggKeys = "channels" | "sentiment" | "sourceAges";
// type OpAggKeys = "avg" | "max" | "median" | "min" | "percent" | "percentile" | "sum";

// interface INode<T> {
//     _buckets: Array<T>;
//     _field_id: string;
//     _field_label: string;
// }

// interface IBucket {
//     _count: number;
//     _key: string;
//     _label: string;
// }

// type CountryAggBucket = {
//     [K in CountryAggKey]: INode<IChoroplethAggBucket>;
// }
// type ChoroplethAggBucket = {
//     [K in ChoroplethAggKeys]: INode<IOpAggBucket>;
// }
// type OpAggBucket = {
//     [K in OpAggKeys]: any;
// }

// interface ICountryAggBucket extends CountryAggBucket, IBucket {};
// interface IChoroplethAggBucket extends ChoroplethAggBucket, IBucket {};
// interface IOpAggBucket extends OpAggBucket, IBucket {};

// interface IAggsResponse {
//     timeseries: INode<ICountryAggBucket>;
//     total: number;
// }

// let t: IAggsResponse;
// t.timeseries._buckets[0].country._buckets[0].sentiment._buckets[0].percent

function ChoroplethMapWidget(props: WithQueryWidgetProps): JSX.Element {
    const mapRef = useRef<null | HTMLDivElement>(null);
    const map = useRef<null | MapboxMap>(null);
    const [longitude, setLongitude] = useState(0);
    const [latitude, setLatitude] = useState(20.0);
    const [zoom, setZoom] = useState(1);

    const apiResponseInfo = getSingleWidgetResponseByName(props.apiResponses, 'choropleth');
    const apiResponse = apiResponseInfo?.responseBody || {};

    // const apiResponse = props.apiResponses?.[0];

    // 1. Setup the Mapbox Map.
    useEffect(() => {
        if (map.current) {
            return;
        }

        map.current = new MapboxMap({
            container: mapRef.current as HTMLDivElement,
            accessToken: import.meta.env.VITE_MAPBOX_ACCESS_TOKEN,
            style: MapboxStyle.Streets,
            center: [longitude, latitude], // starting location: Kansas City
            zoom: zoom,
            maxZoom: 5,
            minZoom: 0,
            preserveDrawingBuffer: true // Required for exporting the map as an image
        });

        // map.current.showTileBoundaries = true;
        // map.current.repaint = true;

        map.current.on("load", () => {
            if (!apiResponse.Countries) {
                // TODO: Why does this get called with no API data?  Shouldn't the wiget wait to display until the data exists?
                return;
            }

            // Add a source for country polygons using the Maobox Countries tileset
            // https://docs.mapbox.com/data/tilesets/reference/mapbox-countries-v1/
            map.current?.addSource("countries", {
                type: "vector",
                url: "mapbox://mapbox.country-boundaries-v1"
            });

            // Define the lookup key for the country shape
            // https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2
            // iso_3166_1 - The two-character country code
            // https://en.wikipedia.org/wiki/ISO_3166-1_alpha-3
            // iso_3166_1_alpha_3 - The three-character country code
            const matchExpression: Expression = [
                "match",
                ["get", "iso_3166_1"]
            ];

            // Map color values for each country
            const countries = apiResponse.Countries._buckets;
            const colorDomain = [0, 0.5, 1];
            const colorRange = [lightModeNamedColors.negative, lightModeNamedColors.mixed, lightModeNamedColors.positive];
            const colorScale = d3.scaleLinear(colorDomain, colorRange);

            if (!countries) {
              return;
            }

            for (let i=0; i< countries?.length; i++) {
                const countryBucket = countries[i];
                const key = countryBucket._key.toUpperCase();

                const positiveSentimentBucket = countryBucket.Sentiment._buckets.find((bucket: any) => bucket._key === "p");
                const sentiment = positiveSentimentBucket?.percent || 0;

                const colorValue = colorScale(sentiment);
                matchExpression.push(key, colorValue);
            }

            // The last value in `matchExpression` is the default color where there is no data
            matchExpression.push("rgba(128, 128, 128, 0.5)");

            map.current?.addLayer({
                id: "countries-example",
                type: "fill",
                source: "countries",
                // This is the specific layer name that we want to add from our tileset.  A tileset can have multiple vector layers embedded within it.
                "source-layer": "country_boundaries",
                paint: {
                    "fill-color": matchExpression
                }
            }, "admin-1-boundary-bg");

            // setTimeout(() => {
            // setInterval(() => {
            //     const matchExpression: Expression = [
            //         "match",
            //         ["get", "iso_3166_1"]
            //     ];
            //     matchExpression.push("US", `rgba(${Math.random()*255}, ${Math.random()*255}, ${Math.random()*255}, 0.5)`)
            //     matchExpression.push("rgba(128, 128, 128, 0.5)");
            //     map.current?.setPaintProperty("countries-example", "fill-color", matchExpression, { validate: true });
            // }, 1000);
        });

        map.current.on("moveend", () => {
            if (map.current) {
                const lng = parseFloat(map.current.getCenter().lng.toFixed(4));
                const lat = parseFloat(map.current.getCenter().lat.toFixed(4));
                const z = parseFloat(map.current.getZoom().toFixed(2));
                setLongitude(lng);
                setLatitude(lat);
                setZoom(z);
            }

            setLongitude(4);
        });
    }, []);

    // Resize the map when Widget moves or resizes
    map.current?.resize();

    return <div ref={mapRef} style={{ width: "100%", height: "100%" }} />
}

export default withQueryWidget(ChoroplethMapWidget, {
    hasSettingsDialog: false,
    hasQueryDialog: true
});

// TIME SERIES
// defaultAggs: {
//     "timeseries": {
//         "op": "histogram",
//         "field": "published",
//         "interval": "day",
//         "aggs": {
//             "Country": {
//                 "op": "keyword",
//                 "field": "country",
//                 "bucket_count": 195,
//                 "aggs": {
//                     // "Age": {
//                     //   // Is this the correct way to handle "age buckets" (e.g. 10-19, 20-29, etc.)
//                     //   "op": "histogram",
//                     //   "field": "source_age",
//                     //   "interval": 10,
//                     // }
//                     // "Channels": {
//                     //   "op": "keyword",
//                     //   "field": "channels",
//                     //   "bucket_count": 3,
//                     //   "aggs": {
//                     //     "percent": {
//                     //       "op": "percent"
//                     //     }
//                     //   }
//                     // },
//                     // "Comments": {
//                     //   "op": "sum",
//                     //   "field": "comments"
//                     // },
//                     // "ContentSubject": {
//                     //   "op": "keyword",
//                     //   "field": "taxonomies",
//                     //   "bucket_count": 3,
//                     //   "aggs": {
//                     //     "percent": {
//                     //       "op": "percent"
//                     //     }
//                     //   }
//                     // },
//                     // "Emotions": {
//                     //   "op": "keyword",
//                     //   "field": "emotions",
//                     //   "aggs": {
//                     //     "percent": {
//                     //       "op": "percent"
//                     //     }
//                     //   }
//                     // },
//                     // "Entities": {
//                     //   "op": "keyword",
//                     //   "field": "entities",
//                     //   "bucket_count": 3,
//                     //   "aggs": {
//                     //     "percent": {
//                     //       "op": "percent"
//                     //     }
//                     //   }
//                     // },
//                     // "Followers": {
//                     //   "op": "sum",
//                     //   "field": "followers"
//                     // },
//                     // "Following": {
//                     //   "op": "sum",
//                     //   "field": "following"
//                     // },
//                     // "Influence": {
//                     //   "op": "sum",
//                     //   "field": "influence"
//                     // },
//                     // "Engagements": {
//                     //   "op": "sum",
//                     //   "field": "total_engagements"
//                     // },
//                     // "Gender": {
//                     //   "op": "keyword",
//                     //   "field": "gender",
//                     //   "aggs": {
//                     //     "percent": {
//                     //       "op": "percent"
//                     //     }
//                     //   }
//                     // },
//                     "Sentiment": {
//                         "op": "keyword",
//                         "field": "sentiment",
//                         "filter": ["p", "n"],
//                         "aggs": {
//                             "percent": {
//                                 "op": "percent"
//                             }
//                         }
//                     },
//                     // "Shares": {
//                     //   "op": "sum",
//                     //   "field": "shares"
//                     // },
//                     // "SourceInterests": {
//                     //   "op": "keyword",
//                     //   "field": "source_interests",
//                     //   "bucket_count": 3,
//                     //   "aggs": {
//                     //     "percent": {
//                     //       "op": "percent"
//                     //     }
//                     //   }
//                     // },
//                     // "Themes": {
//                     //   "op": "keyword",
//                     //   "field": "themes",
//                     //   "bucket_count": 3,
//                     //   "aggs": {
//                     //     "percent": {
//                     //       "op": "percent"
//                     //     }
//                     //   }
//                     // },
//                 }
//             }
//         }
//     }
// }